import { db } from '../firebaseConfig';
import { doc, getDoc, setDoc, updateDoc, collection, getDocs, query, where, serverTimestamp, orderBy, limit } from 'firebase/firestore';

export const getUserType = async (userId) => {
    try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
            return userDoc.data().userType || 'simple';
        }
        return 'simple';
    } catch (error) {
        console.error('Error getting user type:', error);
        return 'simple';
    }
};

export const setUserType = async (userId, userType) => {
    try {
        const userRef = doc(db, 'profiles', userId);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
            // Validate user type
            const validRoles = ['simple', 'pro', 'admin', 'moderator'];
            if (!validRoles.includes(userType)) {
                throw new Error(`Invalid user role: ${userType}`);
            }

            // Update user role
            await updateDoc(userRef, {
                role: userType,
                // Add timestamp for role change
                roleUpdatedAt: new Date()
            });

            return true;
        } else {
            throw new Error('User profile not found');
        }
    } catch (error) {
        console.error('Error setting user type:', error);
        throw error;
    }
};

export const updatePostCount = async (userId) => {
    try {
        const userRef = doc(db, 'profiles', userId);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
            const currentPostCount = userDoc.data().postCount || 0;
            await updateDoc(userRef, {
                postCount: currentPostCount + 1
            });
        }
    } catch (error) {
        console.error('Error updating post count:', error);
    }
};

export const checkUserRole = async (userId) => {
    try {
        const userRef = doc(db, 'profiles', userId);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
            return userDoc.data().role || 'simple';
        }
        
        return 'simple';
    } catch (error) {
        console.error('Error checking user role:', error);
        return 'simple';
    }
};

export const resetAllUserCredits = async () => {
    try {
        const profilesRef = collection(db, 'profiles');
        const querySnapshot = await getDocs(profilesRef);
        
        const batch = [];
        querySnapshot.forEach((document) => {
            const userRef = doc(db, 'profiles', document.id);
            batch.push(
                updateDoc(userRef, {
                    credits: 10
                })
            );
        });

        await Promise.all(batch);
        console.log(`Successfully reset credits for ${batch.length} users`);
        return batch.length;
    } catch (error) {
        console.error('Error resetting user credits:', error);
        throw error;
    }
};

export const isUserProfileOwner = async (currentUserId, targetUsername) => {
    try {
        // Find user by username
        const usersRef = collection(db, 'profiles');
        const q = query(usersRef, where('username', '==', targetUsername));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const profileDoc = querySnapshot.docs[0];
            const profileData = profileDoc.data();

            // Check if current user matches the profile owner
            return profileData.uid === currentUserId;
        }

        return false;
    } catch (error) {
        console.error('Error checking profile ownership:', error);
        return false;
    }
};

export const canEditProfile = async (currentUserId, targetUsername) => {
    try {
        // Check if user is the profile owner or has admin rights
        const isOwner = await isUserProfileOwner(currentUserId, targetUsername);
        if (isOwner) return true;

        // Check user role
        const userRole = await checkUserRole(currentUserId);
        return userRole === 'admin' || userRole === 'moderator';
    } catch (error) {
        console.error('Error checking profile edit permissions:', error);
        return false;
    }
};

export const canViewProfileCredits = async (currentUserId, targetUsername) => {
    try {
        // Check if user is the profile owner
        const isOwner = await isUserProfileOwner(currentUserId, targetUsername);
        if (isOwner) return true;

        // Check user role
        const userRole = await checkUserRole(currentUserId);
        if (userRole === 'admin' || userRole === 'moderator') return true;

        // Find the target profile
        const usersRef = collection(db, 'profiles');
        const q = query(usersRef, where('username', '==', targetUsername));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const profileDoc = querySnapshot.docs[0];
            const profileData = profileDoc.data();

            // Check if current user is a follower or following
            const currentUserDoc = await getDoc(doc(db, 'profiles', currentUserId));
            if (currentUserDoc.exists()) {
                const currentUserData = currentUserDoc.data();
                const followers = profileData.followers || [];
                const following = profileData.following || [];

                return followers.includes(currentUserId) || 
                       following.includes(currentUserId) ||
                       currentUserData.followers?.includes(profileData.uid) ||
                       currentUserData.following?.includes(profileData.uid);
            }
        }

        return false;
    } catch (error) {
        console.error('Error checking profile credits visibility:', error);
        return false;
    }
};

export const processCreditPurchase = async (userId, creditAmount) => {
    try {
        const userRef = doc(db, 'profiles', userId);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
            const currentCredits = userDoc.data().credits || 0;
            const newCreditTotal = currentCredits + creditAmount;
            
            // Update user credits
            await updateDoc(userRef, {
                credits: newCreditTotal,
                lastCreditPurchase: serverTimestamp()
            });

            return newCreditTotal;
        } else {
            throw new Error('User profile not found');
        }
    } catch (error) {
        console.error('Error processing credit purchase:', error);
        throw error;
    }
};

export const getCreditPurchaseHistory = async (userId) => {
    try {
        const purchaseRef = collection(db, 'creditPurchaseRequests');
        const q = query(
            purchaseRef, 
            where('userId', '==', userId),
            orderBy('requestDate', 'desc'),
            limit(10)
        );
        
        const querySnapshot = await getDocs(q);
        
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error fetching credit purchase history:', error);
        return [];
    }
};
